import {
    FileError,
    MAX_FILE_SIZE_BYTES,
    MAX_FILE_SIZE_DEC,
} from "@/config/constants";
import { styles } from "@/config/project";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
    Link,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { filesize } from "filesize";
import { useCallback, useMemo } from "react";
import { FileRejection, useDropzone } from "react-dropzone";
import { useFormContext } from "react-hook-form";
import { VisuallyHiddenInput } from "../form-elements/VisuallyHiddenInput";

export interface IErrorMessage {
    type: FileError;
    message: string;
}

// Define strict type for file reject codes from react-dropzone
export type FileRejectCode =
    | "file-invalid-type"
    | "file-too-large"
    | "too-many-files";

// Strict type for accept mime types using the web standard types
export type AcceptMimeType = "application/pdf" | "text/csv" | string; //

export interface FileDropboxProps {
    /**
     * The form field name when using with react-hook-form
     */
    name?: string;
    /**
     * Maximum file size in bytes
     *
     * @default MAX_FILE_SIZE_BYTES
     */
    maxSize?: number;
    /**
     * Maximum file size in bytes to display in the UI
     *
     * @default MAX_FILE_SIZE_DEC
     */
    maxSizeDisplay?: number;
    /**
     * Accepted file types and extensions
     */
    accept?: Record<AcceptMimeType, string[]>;
    /**
     * Custom icon component
     */
    icon?: React.ReactNode;
    /**
     * Label for upload button
     */
    uploadLabel?: string;
    /**
     * Description text
     */
    description?: string;
    /**
     * Callback when file is selected/changed
     */
    onFileChange?: (file: File | undefined) => void;
}

/**
 * A reusable file dropbox component that allows users to upload files
 */
export const FileDropbox = ({
    name = "document_file",
    maxSize = MAX_FILE_SIZE_BYTES,
    maxSizeDisplay = MAX_FILE_SIZE_DEC,
    accept = { "application/pdf": [".pdf"] },
    icon = (
        <UploadFileIcon
            sx={{ width: 24, height: 24, color: styles.blue, mb: 1 }}
        />
    ),
    uploadLabel = "Click to upload",
    description,
    onFileChange,
}: FileDropboxProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const formContext = useFormContext();
    const hasFormContext = !!formContext;

    // Generate error messages dynamically
    const errorMessages = useMemo(
        () => ({
            "file-invalid-type": {
                type: FileError.InvalidFileType,
                message: `File type must be ${Object.entries(accept)
                    .map(
                        ([type, exts]) =>
                            `${type}${exts.length ? `, ${exts.join(", ")}` : ""}`
                    )
                    .join(" or ")}`,
            },
            "file-too-large": {
                type: FileError.FileTooLarge,
                message: `File is too large. Max size is ${filesize(maxSizeDisplay, { round: 0 })}`,
            },
            "too-many-files": {
                type: FileError.InvalidFileType,
                message: "Too many files selected",
            },
        }),
        [accept, maxSizeDisplay]
    );

    // Get form methods if available
    const {
        setValue,
        clearErrors,
        setError,
        formState: { errors = {} },
    } = formContext || { formState: {} };

    // Handle file attachment when user selects a file
    const handleFileAttach = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = event.target.files?.[0];

        if (file) {
            // set error if file size is greater than max size
            if (file.size > maxSize) {
                if (hasFormContext) {
                    setError(name, errorMessages["file-too-large"]);
                }
                onFileChange?.(undefined);
                return;
            }

            if (hasFormContext) {
                setValue(name, file);
                clearErrors([name, "objectName"]);
            }
            onFileChange?.(file);

            // clears the input value to allow the same file to be attached again if removed
            event.target.value = "";
        }
    };

    const onDrop = useCallback(
        (acceptedFiles: File[], fileRejections: FileRejection[]) => {
            // remove set file
            if (hasFormContext) {
                setValue(name, undefined);
            }

            // If the file is rejected, set an error
            if (
                fileRejections.length > 0 &&
                fileRejections[0].errors.length > 0
            ) {
                const { code } = fileRejections[0].errors[0];
                const mappedError = errorMessages[code as FileRejectCode];

                if (mappedError) {
                    if (hasFormContext) {
                        setError(name, mappedError);
                    }
                    onFileChange?.(undefined);
                    return;
                }
            }

            // If the file is accepted, set the file
            if (acceptedFiles.length > 0) {
                if (hasFormContext) {
                    setValue(name, acceptedFiles[0]);
                    clearErrors(name);
                }
                onFileChange?.(acceptedFiles[0]);
            }
        },
        [
            hasFormContext,
            setValue,
            name,
            errorMessages,
            onFileChange,
            setError,
            clearErrors,
        ]
    );

    // setup dropzone hook
    const { getRootProps } = useDropzone({
        maxFiles: 1,
        accept,
        maxSize,
        onDrop,
    });

    const hasError =
        hasFormContext && errors && (name in errors || "objectName" in errors);

    const defaultDescription =
        description ||
        `${Object.keys(accept)
            .map((type) => type.split("/")[1].toUpperCase())
            .join(", ")} (max. ${filesize(maxSizeDisplay, { round: 0 })})`;

    return (
        <Stack
            py={3}
            px={2}
            width={"100%"}
            alignItems={"center"}
            border={"2px dashed"}
            borderRadius={1}
            borderColor={hasError ? "error.main" : "divider"}
            {...getRootProps()}
        >
            <Stack gap={1} alignItems={"center"} sx={{ width: "100%" }}>
                {icon}

                <Typography>
                    <Link
                        component={"label"}
                        sx={{ color: styles.blue, cursor: "pointer" }}
                    >
                        <VisuallyHiddenInput
                            type={"file"}
                            accept={Object.values(accept).flat().join(",")}
                            onChange={handleFileAttach}
                        />
                        {uploadLabel}
                    </Link>
                    {!isMobile && " or drag and drop"}
                </Typography>

                <Typography variant={"body2"} color={"textSecondary"}>
                    {defaultDescription}
                </Typography>
            </Stack>
        </Stack>
    );
};
